import type { PostureStatus } from '@/components/ui/posture-chip';
import type { Checkpoint } from '@/lib/api/checkpoints';
import { formatDate } from '@/lib/utils/format';

/**
 * Status vocabulary for the IV&V checkpoints tab and drawer.
 *
 * One derivation shared by the table, the KPI tiles and the drawer so a
 * checkpoint never reads differently in two places. Order of precedence:
 *   0. Archived              → archived (kept for history, never scored again)
 *   1. No criteria           → needs criteria (cannot be scored yet)
 *   2. Latest round          → in progress, or its submitted posture
 *   3. No round, due date    → overdue / due soon / scheduled
 *   4. No round, no date     → no date
 */

type CheckpointStateKey =
  | 'archived'
  | 'needs-criteria'
  | 'scoring'
  | 'met'
  | 'conditional'
  | 'missed'
  | 'scored'
  | 'overdue'
  | 'due-soon'
  | 'scheduled'
  | 'no-date';

export interface CheckpointState {
  key: CheckpointStateKey;
  /** null renders the amber "needs criteria" warning chip instead of a posture chip. */
  posture: PostureStatus | null;
  label: string;
  detail: string;
}

/** Keys that surface under the "Needs action" filter. */
export const NEEDS_ACTION: ReadonlySet<CheckpointStateKey> = new Set([
  'needs-criteria',
  'scoring',
  'conditional',
  'missed',
  'overdue',
  'due-soon',
]);

const DUE_SOON_DAYS = 30;
const MS_PER_DAY = 86_400_000;

/** CP-03 for SOW-numbered checkpoints; null for unnumbered ones. */
export function checkpointLabel(c: Pick<Checkpoint, 'sow_number'>): string | null {
  return c.sow_number === null ? null : `CP-${String(c.sow_number).padStart(2, '0')}`;
}

/** Whole days from today (local calendar) to a YYYY-MM-DD date. Negative = past. */
export function daysFromToday(isoDay: string): number {
  const [y, m, d] = isoDay.split('-').map(Number);
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((Date.UTC(y!, m! - 1, d!) - today) / MS_PER_DAY);
}

/**
 * Format a YYYY-MM-DD calendar date as "31 Mar 2027": the app's one date
 * format (C2-45), not the browser locale's. `formatDate` reads a bare
 * calendar date as that day, so the column never shifts a day west of UTC.
 */
export function formatDay(isoDay: string): string {
  return formatDate(isoDay);
}

export function relativeDays(isoDay: string): string {
  const d = daysFromToday(isoDay);
  if (d === 0) return 'today';
  if (d > 0) return `in ${d} ${d === 1 ? 'day' : 'days'}`;
  return `${-d} ${d === -1 ? 'day' : 'days'} ago`;
}

export function checkpointState(c: Checkpoint): CheckpointState {
  const latest = c.latest_assessment;
  if (c.status === 'archived') {
    return {
      key: 'archived',
      posture: 'unassessed',
      label: 'Archived',
      detail:
        c.rounds_count > 0
          ? `${c.rounds_count} ${c.rounds_count === 1 ? 'round' : 'rounds'} kept`
          : 'never scored',
    };
  }
  if (c.criteria.length === 0) {
    return {
      key: 'needs-criteria',
      posture: null,
      label: 'Needs criteria',
      detail: 'add what IV&V assesses',
    };
  }
  if (latest?.status === 'in_progress') {
    return {
      key: 'scoring',
      posture: 'unassessed',
      label: 'Scoring',
      detail: `round ${latest.round_seq} in progress`,
    };
  }
  if (latest) {
    const detail = `round ${latest.round_seq}${latest.submitted_at ? ` · ${formatDay(latest.submitted_at.slice(0, 10))}` : ''}`;
    switch (latest.overall_posture) {
      case 'green':
        return { key: 'met', posture: 'healthy', label: 'Met', detail };
      case 'yellow_divergent':
        return { key: 'conditional', posture: 'at-risk', label: 'Conditional', detail };
      case 'red':
        return { key: 'missed', posture: 'critical', label: 'Missed', detail };
      default:
        return { key: 'scored', posture: 'unassessed', label: 'Scored', detail };
    }
  }
  if (!c.due_date) {
    return { key: 'no-date', posture: 'unassessed', label: 'No date', detail: 'not yet scored' };
  }
  const days = daysFromToday(c.due_date);
  if (days < 0) {
    return {
      key: 'overdue',
      posture: 'critical',
      label: `Overdue ${-days}d`,
      detail: 'not yet scored',
    };
  }
  if (days <= DUE_SOON_DAYS) {
    return {
      key: 'due-soon',
      posture: 'at-risk',
      label: days === 0 ? 'Due today' : `Due in ${days}d`,
      detail: 'not yet scored',
    };
  }
  return { key: 'scheduled', posture: 'unassessed', label: 'Scheduled', detail: 'not yet scored' };
}

/** The summary tiles and header counts on the checkpoints tab. */
export interface CheckpointTileCounts {
  met: number;
  conditional: number;
  /** Latest round scored red (a failed round, C2-23): neither met nor overdue. */
  missed: number;
  overdue: number;
}

/** Counts of active checkpoints by their latest outcome, one derivation for tiles and header. */
export function checkpointTileCounts(checkpoints: ReadonlyArray<Checkpoint>): CheckpointTileCounts {
  const counts: CheckpointTileCounts = { met: 0, conditional: 0, missed: 0, overdue: 0 };
  for (const c of checkpoints) {
    const key = checkpointState(c).key;
    if (key === 'met' || key === 'conditional' || key === 'missed' || key === 'overdue') {
      counts[key] += 1;
    }
  }
  return counts;
}

/**
 * The checkpoint to score next: the earliest overdue scorable checkpoint,
 * otherwise the earliest upcoming unscored one. Null when nothing is pending.
 */
export function nextToScore(checkpoints: ReadonlyArray<Checkpoint>): Checkpoint | null {
  const pending = checkpoints
    .filter((c) => c.criteria.length > 0 && c.due_date !== null)
    .filter((c) => !c.latest_assessment || c.latest_assessment.status === 'in_progress')
    .sort((a, b) => a.due_date!.localeCompare(b.due_date!));
  return pending[0] ?? null;
}
